import React, { FC } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import ControlledSuggestLocation from 'transfix-form/components/ControlledSuggestLocation'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput/ControlledTextInput'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import Box from 'transfix-ui/core/Box'
import {
  StaplesHeadhaulLane,
  StaplesHeadhaulLaneRequest,
  useCreateStaplesHeadhaulLane,
  useUpdateStaplesHeadhaulLane,
} from 'apps/marketplace/apiTypes/staplesHeadhaulLanes'
import CarrierAutosuggest from 'apps/marketplace/components/CarrierAutosuggest/LegacyCarrierAutosuggest'
import { ICarrierResult } from 'apps/marketplace/components/CarrierAutosuggest/types'
import { useStaplesForm } from './hooks/useStaplesForm'
import { TStaplesLaneModalSchema } from './schema/StaplesLaneModalSchema'

export interface IStaplesLaneModalProps extends Pick<IFormModalProps, 'onClose'> {
  onRefetch: () => void
  /**
   * The id of the lane corresponding to the modal
   * Used for edit requests
   */
  laneIdToUpdate: number
  /**
   * Whether this modal should act as the edit modal (if true) or the add modal.
   */
  isEdit: boolean
  /**
   * Data already existing for this particular lane
   * Used to prefill form with existing data if rendering edit modal.
   */
  existingData?: StaplesHeadhaulLane
}

const parseFormParams = (formParams: TStaplesLaneModalSchema): StaplesHeadhaulLaneRequest => {
  const parsedFormParams: StaplesHeadhaulLaneRequest = {
    carrierApplicationId: formParams.carrierApplication?.id,
    pickupCity: formParams.pickupLocation?.city || '',
    pickupStateCode: formParams.pickupLocation?.state || '',
    pickupPostalCode: formParams.pickupPostalCode,
    pickupLatitude: formParams.pickupLatitude,
    pickupLongitude: formParams.pickupLongitude,
    deliveryLatitude: formParams.deliveryLatitude,
    deliveryLongitude: formParams.deliveryLongitude,
    deliveryCity: formParams.deliveryLocation?.city || '',
    deliveryStateCode: formParams.deliveryLocation?.state || '',
    deliveryPostalCode: formParams.deliveryPostalCode,
    dailyTrucks: formParams.dailyTrucks,
    weeklyTrucks: formParams.weeklyTrucks,
    pickupWindow: formParams.pickupWindow,
    deliveryWindow: formParams.deliveryWindow,
    notes: formParams.notes,
  }
  return parsedFormParams
}

const getDefaultValues = (existingData?: StaplesHeadhaulLane): Partial<TStaplesLaneModalSchema> => {
  return {
    pickupLocation: {
      city: existingData?.pickupCity,
      state: existingData?.pickupStateCode,
    },
    deliveryLocation: {
      city: existingData?.deliveryCity,
      state: existingData?.deliveryStateCode,
    },
    carrierApplication: existingData?.carrierApplication || null,
    dailyTrucks: existingData?.dailyTrucks || null,
    weeklyTrucks: existingData?.weeklyTrucks || null,
  }
}

export const StaplesLaneModal: FC<IStaplesLaneModalProps> = ({
  existingData,
  isEdit,
  laneIdToUpdate,
  onClose,
  onRefetch,
}) => {
  const { openToast } = useGlobalToast()
  const { loading: addLoading, mutate: addRequest } = useCreateStaplesHeadhaulLane({})

  const { loading: editLoading, mutate: editRequest } = useUpdateStaplesHeadhaulLane({
    staplesHeadhaulLaneId: laneIdToUpdate,
  })

  const submitRequest = isEdit ? editRequest : addRequest
  const loading = isEdit ? editLoading : addLoading

  const methods = useStaplesForm({
    defaultValues: getDefaultValues(existingData),
  })

  const { control, errors, handleSubmit } = methods

  const successAddResponse = 'Staples headhaul lane successfully added'
  const successEditResponse = 'Staples headhaul lane sucessfully edited'
  const successResponse = isEdit ? successEditResponse : successAddResponse
  const errorCreateResponse = 'An error occured creating this lane'
  const errorEditResponse = 'An error occured editing this lane'
  const errorResponse = isEdit ? errorEditResponse : errorCreateResponse

  const onModalSubmit = async (formParams: TStaplesLaneModalSchema) => {
    const formatRequest = parseFormParams(formParams)
    try {
      await submitRequest(formatRequest)
      openToast({
        toastMessage: successResponse,
        toastType: 'default',
      })

      onRefetch()
      onClose()
    } catch {
      openToast({
        toastMessage: errorResponse,
        toastType: 'alert',
      })
    }
  }

  const fields = useWatch({
    control,
    name: [
      'pickupLocation',
      'deliveryLocation',
      'pickupPostalCode',
      'deliveryPostalCode',
      'carrierApplication',
    ],
  })
  const title = isEdit ? 'Edit Staples Headhaul Lane' : 'Add Staples Headhaul Lane'
  const requiredFields =
    fields.deliveryLocation?.city &&
    fields.deliveryPostalCode &&
    fields.pickupLocation?.city &&
    fields.pickupPostalCode &&
    fields.carrierApplication?.id &&
    fields.carrierApplication?.name != null
  return (
    <FormModal
      open
      title={title}
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'staples-lane-modal-dismiss-button',
          action: onClose,
        },
        {
          label: isEdit ? 'Save Changes' : 'Add Lane',
          type: 'confirm',
          dataTest: 'staples-lane-modal-submit-button',
          action: handleSubmit(onModalSubmit),
          disabled: !requiredFields || loading,
        },
      ]}>
      <Box mb={2}>
        <Box display='flex'>
          <Box width='75%' pr={1} mb={2}>
            <ControlledSuggestLocation
              control={control}
              name='pickupLocation'
              defaultValue={{
                city: existingData?.pickupCity,
                state: existingData?.pickupStateCode,
              }}
              SuggestLocationProps={{
                label: 'Pickup Location',
                dataTest: 'staples-modal-pickup-location',
                resultTypes: ['City', 'PostalCode'],
              }}
            />
          </Box>
          <Box width='25%' mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='pickupPostalCode'
              defaultValue={existingData?.pickupPostalCode}
              TextInputProps={{
                name: 'pickupPostalCode',
                label: 'Pickup Postal Code',
                placeholder: 'Pickup Postal Code',
                dataTest: 'staples-modal-pickup-postal',
                required: true,
              }}
            />
          </Box>
        </Box>

        <Box display='flex'>
          <Box width='75%' mb={2} pr={1}>
            <ControlledSuggestLocation
              control={control}
              name='deliveryLocation'
              defaultValue={{
                city: existingData?.deliveryCity,
                state: existingData?.deliveryStateCode,
              }}
              SuggestLocationProps={{
                label: 'Delivery Location',
                dataTest: 'staples-modal-delivery-location',
                resultTypes: ['City', 'PostalCode'],
              }}
            />
          </Box>

          <Box width='25%' mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='deliveryPostalCode'
              defaultValue={existingData?.deliveryPostalCode}
              TextInputProps={{
                name: 'deliveryPostalCode',
                label: 'Delivery Postal Code',
                placeholder: 'Delivery Postal Code',
                dataTest: 'staples-modal-delivery-postal',
                required: true,
              }}
            />
          </Box>
        </Box>

        <Box mb={2}>
          <Controller
            name='carrierApplication'
            control={control}
            render={({ onChange, value }) => {
              const onCarrierChange = (carrier?: Partial<ICarrierResult>) => {
                onChange({
                  name: carrier?.name || '',
                  id: carrier?.id,
                })
              }
              return (
                <CarrierAutosuggest
                  value={value}
                  id='staples-modal-carrier'
                  dataTest='staples-modal-carrier'
                  defaultValue={{
                    id: existingData?.carrierApplication?.id?.toString() || '',
                    name: existingData?.carrierApplication?.name,
                  }}
                  onChange={onCarrierChange}
                  required
                  large
                />
              )
            }}
          />
        </Box>
        <Box display='flex' mb={2}>
          <Box width='25%' mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='dailyTrucks'
              defaultValue={existingData?.dailyTrucks}
              TextInputProps={{
                name: 'dailyTrucks',
                label: 'Daily Trucks',
                placeholder: '# Daily Trucks',
                invalid: !!errors.dailyTrucks,
                validationMessage: errors.dailyTrucks?.message,
                dataTest: 'staples-modal-daily-trucks',
                type: 'number',
              }}
            />
          </Box>

          <Box width='25%' pr={1} mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='weeklyTrucks'
              defaultValue={existingData?.weeklyTrucks}
              TextInputProps={{
                name: 'weeklyTrucks',
                label: 'Weekly Trucks',
                placeholder: '# Weekly Trucks',
                invalid: !!errors.weeklyTrucks,
                validationMessage: errors.weeklyTrucks?.message,
                dataTest: 'staples-modal-weekly-trucks',
                type: 'number',
              }}
            />
          </Box>

          <Box width='25%' mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='pickupWindow'
              defaultValue={existingData?.pickupWindow}
              TextInputProps={{
                name: 'pickupWindow',
                label: 'Pickup Window',
                placeholder: 'Pickup Window',
                dataTest: 'staples-modal-pickup-window',
              }}
            />
          </Box>

          <Box width='25%' pr={1} mr={2}>
            <DeprecatedControlledTextInput
              control={control}
              name='deliveryWindow'
              defaultValue={existingData?.deliveryWindow}
              TextInputProps={{
                name: 'deliveryWindow',
                label: 'Delivery Window',
                placeholder: 'Delivery Window',
                dataTest: 'staples-modal-delivery-window',
              }}
            />
          </Box>
        </Box>

        <Box display='flex' mb={2} hidden={!isEdit}>
          <Box width='25%' mr={2} hidden={!isEdit}>
            <DeprecatedControlledTextInput
              control={control}
              name='pickupLatitude'
              defaultValue={existingData?.pickupLatitude}
              TextInputProps={{
                name: 'pickupLatitude',
                label: 'Pickup Latitude',
                placeholder: 'Pickup Latitude',
                dataTest: 'staples-modal-pickup-latitude',
              }}
            />
          </Box>

          <Box width='25%' pr={1} mr={2} hidden={!isEdit}>
            <DeprecatedControlledTextInput
              control={control}
              name='pickupLongitude'
              defaultValue={existingData?.pickupLongitude}
              TextInputProps={{
                name: 'pickupLongitude',
                label: 'Pickup Longitude',
                placeholder: 'Pickup Longitude',
                dataTest: 'staples-modal-pickup-longitude',
              }}
            />
          </Box>

          <Box width='25%' mr={2} hidden={!isEdit}>
            <DeprecatedControlledTextInput
              control={control}
              name='deliveryLatitude'
              defaultValue={existingData?.deliveryLatitude}
              TextInputProps={{
                name: 'deliveryLatitude',
                label: 'Delivery Latitude',
                placeholder: 'Delivery Latitude',
                dataTest: 'staples-modal-delivery-latitude',
              }}
            />
          </Box>

          <Box width='25%' pr={1} mr={2} hidden={!isEdit}>
            <DeprecatedControlledTextInput
              control={control}
              name='deliveryLongitude'
              defaultValue={existingData?.deliveryLongitude}
              TextInputProps={{
                name: 'deliveryLongitude',
                label: 'Delivery Longitude',
                placeholder: 'Delivery Longitude',
                dataTest: 'staples-modal-delivery-longitude',
              }}
            />
          </Box>
        </Box>

        <Box mb={2}>
          <ControlledTextArea
            control={control}
            name='notes'
            defaultValue={existingData?.notes}
            TextAreaProps={{
              name: 'notes',
              label: 'Notes',
              placeholder: 'Notes',
              dataTest: 'staples-modal-notes',
            }}
          />
        </Box>
      </Box>
    </FormModal>
  )
}
